import { useMemo } from 'react';
import { DROPDOWN_OPTIONS } from './components/ClientSettingsForm/ClientSettingsForm.meta';
import {
  CashflowEnginesOptionDTO,
  PlanStartYearDTO,
  SettingsDTO,
  useClientSettingsQuery,
} from './api/useClientSettingsQuery';
import { OptionType } from '../CommonComponents/OPL/Select/BaseSelect/types';
import { RiskToleranceQuestionnaireType } from '../CommonSelectors/CommonArea.types';
import { CMAType } from '../AdviserSettings/Page/CustomModelAssumptions/types';

export interface AsyncData {
  options: {
    remainingCashflowModeOptions: OptionType<number>[];
    portfolioReturnsOptions: OptionType<number | null>[];
    assetBucketingOptions: OptionType<boolean | null>[];
    managedAccountsGroupingOptions: OptionType<boolean | null>[];
    cashflowEngines: CashflowEnginesOptionDTO[];
    assumeCurrentTaxRateProvisionsExpireOptions: OptionType<boolean>[];
    beFi20Options: OptionType<boolean | null>[];
    pulseCheckOptions: OptionType<boolean | null>[];
    riskToleranceWorkflowOptions: OptionType<RiskToleranceQuestionnaireType | null>[];
    capitalMarketAssumptionsOptions: OptionType<CMAType | null>[];
    planStartYearOptions: OptionType<PlanStartYearDTO | null>[];
  };
  settings: SettingsDTO;
}

export const managedAccountsGroupingOptions = isGrouped => [
  {
    label: `Use Advisor settings - ${isGrouped ? 'Portfolio Group' : 'None'}`,
    value: null,
  },
  { label: 'None', value: false },
  { label: 'Portfolio Group', value: true },
];

export const useClientSettings = () => {
  const { isLoading, isError, refetch, data } = useClientSettingsQuery();

  const asyncData: AsyncData | null = useMemo(() => {
    if (!data) {
      return null;
    }

    const { Settings, Options } = data;

    const options = {
      ...DROPDOWN_OPTIONS,
      capitalMarketAssumptionsOptions: data.Settings.ShowCmaCustomAssumptions
        ? DROPDOWN_OPTIONS.capitalMarketAssumptionsOptions
        : DROPDOWN_OPTIONS.capitalMarketAssumptionsOptions.filter(
            option => option.value !== CMAType.CustomCMAs,
          ),
      managedAccountsGroupingOptions: managedAccountsGroupingOptions(
        Settings.InheritedManagedAccountGrouping,
      ),
      cashflowEngines: Options.CashflowEngines,
    };

    return {
      options,
      settings: Settings,
    };
  }, [data]);

  return {
    isLoading,
    isError,
    refetch,
    data: asyncData,
  };
};
